// Product-owner dashboard (route: /analytics). Deployed to dev via the frontend pipeline.
// re-deploy: rebuild frontend from deploy-dev-myhelp so /analytics is live on dev.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PageToggle } from "@me8eon/page_toggle";
import { useTranslation } from "@me8eon/translation";
import { useJwtToken } from "@me8eon/react_authentication";
import { EonUiButton, EonUiDatePicker, EonUiIcon } from "@eon-ui/eon-ui-components-react";
import {
    DashboardTabKey,
    SelectedCategory,
    TimeRange,
    currentLocale,
} from "./poData";
import {
    fetchLastRun,
    fetchTaxonomyCategories,
    fetchCompanies,
    Company,
    triggerSync,
    toWindow,
    DashboardLastRun,
    DashboardRange,
} from "../../services/dashboardApi";
import OverviewTab from "./tabs/OverviewTab";
import TotalQueriesTab from "./tabs/TotalQueriesTab";
import IncidentsTab from "./tabs/IncidentsTab";
import UserSentimentTab from "./tabs/UserSentimentTab";
import ProductsTab from "./tabs/ProductsTab";
import RequestBreakdownTab from "./tabs/RequestBreakdownTab";
import "./ProductOwnerDashboard.scss";
import MultiSelectDropdown from "../../components/multi-select-dropdown/MultiSelectDropdown";
import { DropdownItem } from "../../components/multi-select-dropdown/MultiSelectDropdown.types";

const TIME_RANGES: TimeRange[] = ["7 days", "15 days", "30 days"];

// Max span (in days, inclusive) selectable via the custom "Range" picker. 
const CUSTOM_RANGE_MAX_DAYS = 30;

type CustomRange = { from: string; to: string };




// Parses a "yyyy-MM-dd" string as local midnight (matches the backend's startOfDay).
function parseLocalDate(iso: string): Date {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
}

// "yyyy-MM-dd" for today, matching the pickers' selectToday default so the draft state stays
// in sync even before the user actively changes a date.
function todayISO(): string {
    const d = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// "yyyy-MM-dd" for `daysAgo` local calendar days before today (matches the backend's
// localDateISO) — calendar-based, not ms subtraction, so it can't drift across a DST change.
function daysAgoISO(daysAgo: number): string {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysAgo);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// The backend windows on local calendar days [today-(N-1), today] inclusive (dashboard.
// controller.ts's resolveRange) — mirror that here so the caption matches what was queried.
function formatWindowRange(windowParam: string): string {
    const days = parseInt(windowParam, 10);
    if (!days) return "";
    const start = parseLocalDate(daysAgoISO(days - 1));
    const end = parseLocalDate(todayISO());
    const sameYear = start.getFullYear() === end.getFullYear();
    const loc = currentLocale();
    const startStr = start.toLocaleDateString(loc, {
        month: "short",
        day: "numeric",
        ...(sameYear ? {} : { year: "numeric" }),
    });
    const endStr = end.toLocaleDateString(loc, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
    return `${startStr} – ${endStr}`;
}

// Inclusive day span: `from` counted from its local start (00:00:00.000), `to` counted to its
// local end (23:59:59.999) — so from === to (a single day) returns 1, not 0.
function daysBetween(fromISO: string, toISO: string): number {
    const start = parseLocalDate(fromISO).getTime();
    const end = parseLocalDate(toISO).getTime() + 86_400_000 - 1;
    return Math.ceil((end - start) / 86_400_000);
}

// Formats a custom {from,to} range the same way as the data-freshness "actual range" text.
function formatCustomRange(range: CustomRange): string {
    const from = parseLocalDate(range.from);
    const to = parseLocalDate(range.to);
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return "";
    const sameYear = from.getFullYear() === to.getFullYear();
    const loc = currentLocale();
    const fromStr = from.toLocaleDateString(
        loc,
        sameYear
            ? { month: "short", day: "numeric" }
            : { month: "short", day: "numeric", year: "numeric" }
    );
    const toStr = to.toLocaleDateString(loc, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
    return `${fromStr} – ${toStr}`;
}

const ProductOwnerDashboard: React.FC = () => {
    const t = useTranslation();
    const ts = (key: string) => t(`productDashboard.shell.${key}`);
    const tc = (key: string) => t(`productDashboard.common.${key}`);
    const tcompany = (key: string) => t(`productDashboard.companyFilter.${key}`);
    const tcluster = (key: string) => t(`productDashboard.clusterFilter.${key}`);
    const tcategory = (key: string) => t(`productDashboard.categoryFilter.${key}`);
    const [activeTab, setActiveTab] = useState<DashboardTabKey>("overview");
    const [categoryFilter, setCategoryFilter] = useState<SelectedCategory[]>([]);
    const [companyFilter, setCompanyFilter] = useState<DropdownItem[]>([]);
    const [timeRange, setTimeRange] = useState<TimeRange>("30 days");
    const [search, setSearch] = useState<string>("");

    // Custom "Range" picker: an applied {from,to} range (overrides the preset pills when set),
    // plus the draft from/to being edited in the open panel.
    const [customRange, setCustomRange] = useState<CustomRange | null>(null);
    const [rangePickerOpen, setRangePickerOpen] = useState(false);
    const [draftFrom, setDraftFrom] = useState(todayISO());
    const [draftTo, setDraftTo] = useState(todayISO());
    const [rangeError, setRangeError] = useState<string | null>(null);
    const rangeWrapRef = useRef<HTMLDivElement>(null);

    // Close the range editor on outside click (same pattern as MultiSelectDropdown).
    useEffect(() => {
        if (!rangePickerOpen) return;
        const onDocClick = (e: MouseEvent) => {
            if (
                rangeWrapRef.current &&
                !rangeWrapRef.current.contains(e.target as Node)
            ) {
                setRangePickerOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [rangePickerOpen]);

    // Data freshness (from the load_run audit). Falls back silently if unavailable.
    const idToken = useJwtToken();
    const [lastRun, setLastRun] = useState<DashboardLastRun | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState<string | null>(null);
    // Bumped when a sync finishes → keys the tab container so the active tab remounts
    // and re-fetches its KPIs / categories / sentiment with the freshly loaded data.
    const [dataVersion, setDataVersion] = useState(0);
    useEffect(() => {
        fetchLastRun(idToken).then(setLastRun).catch(() => undefined);
    }, []);

    // Full taxonomy for the "All Categories" filter dropdown — fetched once from the backend
    // instead of bundling the ~43-category list into the frontend build.
    const [taxonomyCategories, setTaxonomyCategories] = useState<SelectedCategory[]>([]);
    useEffect(() => {
        fetchTaxonomyCategories(idToken).then(setTaxonomyCategories).catch(() => undefined);
    }, []);

    // Company/cluster reference list for the company filter dropdown — fetched once from the
    // backend instead of bundling the ~200-entry list into the frontend build.
    const [companies, setCompanies] = useState<Company[]>([]);
    useEffect(() => {
        fetchCompanies(idToken).then(setCompanies).catch(() => undefined);
    }, []);

    const companyOptions = useMemo<DropdownItem[]>(
        () => [...companies].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })).map((c) => ({ label: c.name, value: c.id })),
        [companies]
    );
    // Company name lookup by id — the dropdown keys on id, but the API filters on name.
    const companyNameById = useMemo<Record<string, string>>(
        () =>
            companies.reduce((acc, c) => {
                acc[c.id] = c.name;
                return acc;
            }, {} as Record<string, string>),
        [companies]
    );
    // Companies grouped by cluster, so toggling a cluster can add/remove all its companies at once.
    const companiesByCluster = useMemo<Record<string, DropdownItem[]>>(
        () =>
            companies.reduce((acc, c) => {
                (acc[c.cluster] ??= []).push({ label: c.name, value: c.id });
                return acc;
            }, {} as Record<string, DropdownItem[]>),
        [companies]
    );
    const clusterOptions = useMemo<DropdownItem[]>(
        () => Object.keys(companiesByCluster).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })).map((cluster) => ({ label: cluster, value: cluster })),
        [companiesByCluster]
    );

    // While a sync runs, poll the freshness endpoint until the server reports it finished.
    useEffect(() => {
        if (!syncing) return;
        const id = setInterval(() => {
            fetchLastRun(idToken)
                .then((lr) => {
                    setLastRun(lr);
                    if (!lr.running) {
                        setSyncing(false);
                        if (lr.lastSyncError) {
                            // A job's spawn/run actually failed — say so instead of claiming success.
                            setSyncMsg(`${ts("syncCompletedWithErrors")} ${lr.lastSyncError}`);
                        } else {
                            setSyncMsg(ts("dataRefreshed"));
                            setDataVersion((v) => v + 1); // remount tabs → re-fetch fresh data
                        }
                    }
                })
                .catch(() => undefined); // keep polling on transient errors
        }, 5000);
        return () => clearInterval(id);
    }, [syncing, idToken]);

    // Admin-only: kick off an on-demand incremental load, then poll until done.
    const onSync = async () => {
        try {
            setSyncMsg(null);
            setSyncing(true);
            const r = await triggerSync(idToken);
            setSyncMsg(r.message);
        } catch {
            setSyncing(false);
            setSyncMsg(ts("syncFailed"));
        }
    };

    // Category selected for drill-down (e.g. from Overview → opens on the Total Queries tab).
    const [drillCategory, setDrillCategory] = useState<SelectedCategory | null>(null);
    const goTo = (key: DashboardTabKey, category?: SelectedCategory) => {
        setDrillCategory(category ?? null);
        setActiveTab(key);
    };
    const goBack = () => setActiveTab("overview");

    // Selecting a preset pill (7/15/30 days) drops any applied custom range.
    const selectPresetRange = (r: TimeRange) => {
        setTimeRange(r);
        setCustomRange(null);
        setRangePickerOpen(false);
    };

    const openRangeEditor = () => {
        // Only reseed the drafts from an applied range — otherwise leave them as the user left
        // them (selecting a preset window clears customRange but shouldn't wipe the drafts).
        if (customRange) {
            setDraftFrom(customRange.from);
            setDraftTo(customRange.to);
        }
        setRangeError(null);
        setRangePickerOpen(true);
    };
    const toggleRangeEditor = () =>
        rangePickerOpen ? setRangePickerOpen(false) : openRangeEditor();

    const applyCustomRange = () => {
        if (!draftFrom || !draftTo) {
            setRangeError(ts("rangeSelectBoth"));
            return;
        }
        if (draftFrom > draftTo) {
            setRangeError(ts("rangeInvalidOrder"));
            return;
        }
        if (daysBetween(draftFrom, draftTo) > CUSTOM_RANGE_MAX_DAYS) {
            setRangeError(
                `${ts("rangeTooWide")} ${CUSTOM_RANGE_MAX_DAYS} ${tc("days")}.`
            );
            return;
        }
        setCustomRange({ from: draftFrom, to: draftTo });
        setRangePickerOpen(false);
        setRangeError(null);
    };

    const clearCustomRange = () => {
        setCustomRange(null);
        setRangePickerOpen(false);
    };

    // A cluster reads as "checked" only when every one of its companies is currently selected.
    const companySelectedValues = new Set(companyFilter.map((c) => c.value));
    const clusterFilter = clusterOptions.filter((cluster) =>
        (companiesByCluster[cluster.value] ?? []).every((c) => companySelectedValues.has(c.value))
    );

    // Checking/unchecking a cluster adds/removes all of its companies from the company filter.
    const onClusterFilterChange = (next: DropdownItem[]) => {
        const prevValues = new Set(clusterFilter.map((c) => c.value));
        const nextValues = new Set(next.map((c) => c.value));
        const added = next.filter((c) => !prevValues.has(c.value));
        const removed = clusterFilter.filter((c) => !nextValues.has(c.value));

        setCompanyFilter((prev) => {
            let updated = prev;
            added.forEach((cluster) => {
                const clusterCompanies = companiesByCluster[cluster.value] ?? [];
                const existing = new Set(updated.map((c) => c.value));
                updated = [...updated, ...clusterCompanies.filter((c) => !existing.has(c.value))];
            });
            removed.forEach((cluster) => {
                const clusterCompanyValues = new Set((companiesByCluster[cluster.value] ?? []).map((c) => c.value));
                updated = updated.filter((c) => !clusterCompanyValues.has(c.value));
            });
            return updated;
        });
    };

    // True once the user has diverged from the defaults on any of the three filters
    // (category, company, time range/custom range) — drives the "Clear all filters" button.
    const hasActiveFilters =
        categoryFilter.length > 0 || companyFilter.length > 0 || customRange !== null || timeRange !== "30 days";

    const clearAllFilters = () => {
        setCategoryFilter([]);
        setCompanyFilter([]);
        setTimeRange("30 days");
        setCustomRange(null);
        setRangePickerOpen(false);
        setRangeError(null);
    };

    // Preset window param, only relevant when no custom range is applied.
    const windowParam = toWindow(timeRange);

    // Plain company-name list for the API/hook layer — decoupled from the dropdown's {label,value} shape.
    const companyNames = companyFilter.map((c) => companyNameById[c.value]).filter((name): name is string => Boolean(name));

    // What the UI shows for the period: exact day count for a custom range, else the preset window.
    const periodDays = customRange ? daysBetween(customRange.from, customRange.to) : Number(windowParam);
    const isCustomPeriod = customRange !== null;

    // What drives data fetches: the real from/to for a custom range, or the preset window otherwise.
    const range: DashboardRange = customRange ? { from: customRange.from, to: customRange.to } : { window: windowParam };

    // How many days of data we actually hold = span from the oldest to the newest turn (inclusive).
    const daysOfData = (() => {
        if (!lastRun?.earliestDataAt || !lastRun?.latestDataAt) return null;
        const ms =
            new Date(lastRun.latestDataAt).getTime() -
            new Date(lastRun.earliestDataAt).getTime();
        if (!Number.isFinite(ms) || ms < 0) return null;
        return Math.floor(ms / 86_400_000) + 1;
    })();

    // Actual calendar range of the data we hold (oldest → newest turn), e.g. "Jun 30 – Jul 7, 2026".
    const dataRange = (() => {
        if (!lastRun?.earliestDataAt || !lastRun?.latestDataAt) return null;
        const from = new Date(lastRun.earliestDataAt);
        const to = new Date(lastRun.latestDataAt);
        if (isNaN(from.getTime()) || isNaN(to.getTime())) return null;
        const sameYear = from.getFullYear() === to.getFullYear();
        const loc = currentLocale();
        const fromStr = from.toLocaleDateString(
            loc,
            sameYear
                ? { month: "short", day: "numeric" }
                : { month: "short", day: "numeric", year: "numeric" }
        );
        const toStr = to.toLocaleDateString(loc, {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
        return `${fromStr} – ${toStr}`;
    })();

    const renderTab = () => {
        switch (activeTab) {
            case "overview":
                return (
                    <OverviewTab
                        onNavigate={goTo}
                        range={range}
                        periodDays={periodDays}
                        isCustomPeriod={isCustomPeriod}
                        categoryFilter={categoryFilter}
                        companyFilter={companyNames}
                    />
                );
            case "totalQueries":
                return (
                    <TotalQueriesTab
                        initialCategory={drillCategory}
                        range={range}
                        periodDays={periodDays}
                        isCustomPeriod={isCustomPeriod}
                        categoryFilter={categoryFilter}
                        companyFilter={companyNames}
                    />
                );
            case "incidents":
                return (
                    <IncidentsTab
                        range={range}
                        periodDays={periodDays}
                        isCustomPeriod={isCustomPeriod}
                        categoryFilter={categoryFilter}
                        companyFilter={companyNames}
                    />
                );
            case "userSentiment":
                return (
                    <UserSentimentTab
                        range={range}
                        periodDays={periodDays}
                        isCustomPeriod={isCustomPeriod}
                        categoryFilter={categoryFilter}
                        companyFilter={companyNames}
                    />
                );
            case "products":
                return <ProductsTab />;
            case "callbackRequests":
                return (
                    <RequestBreakdownTab
                        kind="callback"
                        range={range}
                        periodDays={periodDays}
                        isCustomPeriod={isCustomPeriod}
                        categoryFilter={categoryFilter}
                        companyFilter={companyNames}
                    />
                );
            case "liveChatRequests":
                return (
                    <RequestBreakdownTab
                        kind="liveChat"
                        range={range}
                        periodDays={periodDays}
                        isCustomPeriod={isCustomPeriod}
                        categoryFilter={categoryFilter}
                        companyFilter={companyNames}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="po-dashboard">
            <PageToggle />

            {/* Filter bar */}
            <div className="po-filterbar">
                <div className="po-filter-left">
                    <div className="po-filter-dropdowns">
                        <MultiSelectDropdown
                            key="category-filter"
                            options={taxonomyCategories.map((c) => ({
                                label: c.label,
                                value: `${c.parent}/${c.child}`,
                            }))}
                            selected={categoryFilter.map((c) => ({
                                label: c.label,
                                value: `${c.parent}/${c.child}`,
                            }))}
                            onChange={(items) =>
                                setCategoryFilter(
                                    items.map((i) => {
                                        const [parent, child] =
                                            i.value.split("/");
                                        return {
                                            parent,
                                            child,
                                            label: i.label,
                                        };
                                    })
                                )
                            }
                            allLabel={tcategory("allCategories")}
                            allLabelShort={tcategory("allCategoriesShort")}
                            selectedSuffix={tcategory("selectedSuffix")}
                            ariaLabel={tcategory("filterAria")}
                            searchPlaceholder={tcategory("searchPlaceholder")}
                            noMatchPrefix={tcategory("noMatchPrefix")}
                        />
                        <MultiSelectDropdown
                            key="cluster-filter"
                            options={clusterOptions}
                            selected={clusterFilter}
                            onChange={onClusterFilterChange}
                            allLabel={tcluster("allClusters")}
                            allLabelShort={tcluster("allClustersShort")}
                            selectedSuffix={tcluster("selectedSuffix")}
                            ariaLabel={tcluster("filterAria")}
                            searchPlaceholder={tcluster("searchPlaceholder")}
                            noMatchPrefix={tcluster("noMatchPrefix")}
                        />
                        <MultiSelectDropdown
                            key="company-filter"
                            options={companyOptions}
                            selected={companyFilter}
                            onChange={setCompanyFilter}
                            allLabel={tcompany("allCompanies")}
                            allLabelShort={tcompany("allCompaniesShort")}
                            selectedSuffix={tcompany("selectedSuffix")}
                            ariaLabel={tcompany("filterAria")}
                            searchPlaceholder={tcompany("searchPlaceholder")}
                            noMatchPrefix={tcompany("noMatchPrefix")}
                        />
                    </div>
                    {lastRun && (
                        <div className="po-datafreshness">
                            <span className="po-datafreshness__text">
                                {ts("dataUpdated")}{" "}
                                <strong>
                                    {lastRun.lastRunAt
                                        ? new Date(
                                              lastRun.lastRunAt
                                          ).toLocaleString(currentLocale())
                                        : "—"}
                                </strong>
                                {` · ${ts("latestTurn")} `}
                                {lastRun.latestDataAt
                                    ? new Date(
                                          lastRun.latestDataAt
                                      ).toLocaleString(currentLocale())
                                    : "—"}
                                {" · "}
                                {lastRun.totalRows.toLocaleString(
                                    currentLocale()
                                )}{" "}
                                {ts("records")}
                                <strong> {ts("myhelpData")}</strong>
                            </span>
                            {lastRun.isAdmin && (
                                <button
                                    type="button"
                                    className="po-refresh-btn"
                                    onClick={onSync}
                                    disabled={syncing || lastRun.running}
                                >
                                    {syncing || lastRun.running
                                        ? ts("syncing")
                                        : ts("refresh")}
                                </button>
                            )}
                            {syncMsg && (
                                <span className="po-datafreshness__ok">
                                    {syncMsg}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div className="po-timerange-wrap" ref={rangeWrapRef}>
                    <div
                        className="po-timerange"
                        role="group"
                        aria-label={ts("timeRangeAria")}
                    >
                        {TIME_RANGES.map((r) => (
                            <button
                                key={r}
                                className={`po-timerange__btn ${!customRange && timeRange === r ? "is-active" : ""}`}
                                onClick={() => selectPresetRange(r)}
                            >
                                {r}
                            </button>
                        ))}
                        {customRange ? (
                            <div className="po-timerange__btn po-timerange__chip is-active">
                                <button
                                    type="button"
                                    className="po-timerange__chip-label"
                                    onClick={toggleRangeEditor}
                                >
                                    <EonUiIcon
                                        name="calendar"
                                        size="small"
                                        scheme="neutral100"
                                    />
                                    {formatCustomRange(customRange)}
                                </button>
                                <button
                                    type="button"
                                    className="po-timerange__chip-clear"
                                    aria-label={ts("rangeClearAria")}
                                    onClick={clearCustomRange}
                                >
                                    <EonUiIcon
                                        name="cross"
                                        size="small"
                                        scheme="neutral100"
                                    />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="po-timerange__btn po-timerange__btn--range"
                                aria-label={ts("rangeAria")}
                                onClick={toggleRangeEditor}
                            >
                                <span className="po-timerange__chip-label">
                                    <EonUiIcon name="calendar" size="small" />
                                </span>
                                {ts("rangeLabel")}
                            </button>
                        )}
                    </div>
                    <span className="po-timerange__caption">
                        {customRange
                            ? `${ts("dateRangeCaption")} · ${formatCustomRange(customRange)}`
                            : `${tc("last")} ${timeRange} · ${formatWindowRange(windowParam)}`}
                    </span>
                    <div
                        className={`po-rangepicker ${rangePickerOpen ? "" : "po-rangepicker--hidden"}`}
                    >
                        <div className="po-rangepicker__row">
                            <div className="po-rangepicker__field">
                                <EonUiDatePicker
                                    value={draftFrom}
                                    onValueChanged={(e) =>
                                        setDraftFrom(e.target.value)
                                    }
                                    name="po-range-from"
                                    size="small"
                                    label={ts("rangeFrom")}
                                    labelOutside={false}
                                    min={daysAgoISO(CUSTOM_RANGE_MAX_DAYS - 1)}
                                    max={draftTo || todayISO()}
                                    selectToday={true}
                                    dateFormat="yyyy-MM-dd"
                                />
                            </div>
                            <div className="po-rangepicker__field">
                                <EonUiDatePicker
                                    value={draftTo}
                                    onValueChanged={(e) =>
                                        setDraftTo(e.target.value)
                                    }
                                    name="po-range-to"
                                    size="small"
                                    label={ts("rangeTo")}
                                    labelOutside={false}
                                    min={draftFrom || daysAgoISO(CUSTOM_RANGE_MAX_DAYS - 1)}
                                    max={todayISO()}
                                    selectToday={true}
                                    dateFormat="yyyy-MM-dd"
                                />
                            </div>
                            <button
                                type="button"
                                className="po-rangepicker__apply"
                                aria-label={ts("rangeApplyAria")}
                                onClick={applyCustomRange}
                            >
                                <EonUiIcon
                                    name="chevron_small_right"
                                    size="small"
                                    scheme="neutral100"
                                />
                            </button>
                        </div>
                        {rangeError && (
                            <span className="po-rangepicker__error">
                                {rangeError}
                            </span>
                        )}
                    </div>
                </div>

                {hasActiveFilters && (
                    <div className="po-clearall">
                        <EonUiButton
                            rank="secondary"
                            size="small"
                            text={tc("clearAllFilters")}
                            onClick={clearAllFilters}
                        />
                    </div>
                )}
            </div>

            {/* Page bar: simple title on Overview, back breadcrumb on a section */}
            <div className="po-pagebar">
                {activeTab === "overview" ? (
                    <span className="po-pagebar__title">{ts("overview")}</span>
                ) : (
                    <button
                        type="button"
                        className="po-pagebar__back"
                        onClick={goBack}
                    >
                        <span className="po-pagebar__arrow">←</span>{" "}
                        {ts("overview")}
                    </button>
                )}
            </div>

            <div className="po-content" key={dataVersion}>
                {renderTab()}
            </div>
        </div>
    );
};

export default ProductOwnerDashboard;
